import { type FC, type ReactNode } from "react";
import { type ActiveStorageBlob } from "react-activestorage";

import { FileManager } from "@styled/file_manager";
import { FilePicker } from "@styled/file_picker";
import { FilePickerGuide } from "@styled/file_picker_guide";
import { FileQueue } from "@styled/file_queue";

import { type ReportPaneCopy } from "./final_report_pane";
import { renderFileQueueItem } from "./shared_card_utils";
import { TRANSCRIPT_ACCEPT } from "./transcripts_pane";

// The Reports (or Drafting) and Dictation panels of the guided empty state. Both render the
// SAME FilePicker + FilePickerGuide that FinalReportPane and TranscriptsPane
// already show at zero documents — copy included, so the guided state and the
// tabbed view never diverge. Records is not here: it reuses ProcessRecordsCard
// whole, because record uploads run the review workflow rather than attaching.

// Panel head. Matches IntakePane's type scale so the three panels read as one
// interface, without nesting IntakePane's own bordered container inside the
// guided panel — a box within a box is the same stacked-accent problem the
// amber frame had.
const PanelHead: FC<{ title: string; subtitle: ReactNode }> = ({
  title,
  subtitle,
}) => (
  <>
    <h3 className="text-[15px] font-semibold text-slate-900">{title}</h3>
    <p className="mb-4 text-xs text-slate-500">{subtitle}</p>
  </>
);

export const GuidedReportPanel: FC<{
  copy: ReportPaneCopy;
  save: (blob: ActiveStorageBlob) => void;
  // Another way to start, in the upload card under its action, and the card's last line.
  secondary?: ReactNode;
  footnote?: ReactNode;
}> = ({ copy, save, secondary, footnote }) => (
  <FileManager save={save}>
    <PanelHead title={copy.title} subtitle={copy.subtitle} />

    <FilePicker
      title={copy.hero.title}
      action={copy.hero.action}
      note={copy.hero.note}
      accept={copy.accept}
      secondary={secondary}
      footnote={footnote}
    >
      <FilePickerGuide steps={copy.steps} />
    </FilePicker>

    <FileQueue render={renderFileQueueItem} />
  </FileManager>
);

export const GuidedDictationPanel: FC<{
  save: (blob: ActiveStorageBlob) => void;
}> = ({ save }) => (
  <FileManager save={save}>
    <PanelHead
      title="Dictation & notes"
      subtitle="Turn audio or notes into a clean transcript."
    />

    <FilePicker
      title="Upload audio or dictation notes"
      action="Upload audio or notes"
      accept={TRANSCRIPT_ACCEPT}
    >
      <FilePickerGuide
        steps={[
          {
            label: "Upload audio or notes",
            description: "MP3, WAV, M4A, MP4, or DOCX",
          },
          {
            label: "We transcribe and clean",
            description: "Audio → text, then report-style cleanup",
          },
          {
            label: "Download Word transcript",
            description: "DOCX ready in your project",
          },
        ]}
      />
    </FilePicker>

    <FileQueue render={renderFileQueueItem} />
  </FileManager>
);
